import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Core data
  instructions: [],
  selectedStep: null,

  // Multi-step selection (membership set + anchor for shift-click pivot)
  selectedStepIds: [],
  selectionAnchorId: null,

  // Right panel view machine
  rightPanelView: 'INSTRUCTIONS_VIEW', // 'INSTRUCTIONS_VIEW' | 'STEP_EDITOR_VIEW' | 'EXECUTION_VIEW'

  // UI flags
  isGenerating: false,
  isSavedExecutionRecord: false,
  testCompleted: false,
  /** True while saved testcase plan is loading or merging into instructions. */
  isLoadingSavedPlan: false,

  // Environment & AI model
  selectedEnvironment: null,
  selectedAIModel: null,
  /** True when toolbar model dropdown is on Auto (see AIModelRuntimeSelector second callback arg). */
  selectedAIModelIsAuto: true,

  // Replay state
  replayProgressMap: {},
  replayExecutionResults: null,
  isReplayExecuting: false,
  replayCurrentStepIndex: -1,
  flatStepsForReplay: [],

  // Context flags
  isLoadedFromTestcase: false,
  hasExecutedInSession: false,

  // Data passed to deep leaf components
  fileSuggestions: [],
  fileDescriptions: {},
  mcpToolsList: [],
  mcpToolMap: {},

  // Execution results
  executionResults: null,
  overallStatus: null,
  automationError: null,
  screenshots: [],
  logs: [],
  executionFlow: [],
};

/** Shared by resetExecutionRun and prepareForReplayRun (Immer draft state). */
const RECORDING_PLAN_STATUSES = new Set([
  'pending',
  'completed',
  'failed',
  'failed_by_evaluation',
]);

function stripExecutionRuntimeAndReplayState(state) {
  state.instructions = state.instructions.map(inst => ({
    ...inst,
    status: undefined,
    aiSteps: (inst.aiSteps ?? []).map(step => {
      const {
        status, executionGroup, passed, confidence,
        actualOutcome, actual_outcome,
        details, error, reason, failureReason, failure_reason,
        duration, screenshot, resultMessage, result,
        ...cleanStep
      } = step;
      // Keep recording-time plan markers so replay skip UI stays accurate after prepareForReplayRun.
      const preservedStatus =
        status && RECORDING_PLAN_STATUSES.has(status) ? status : undefined;
      return preservedStatus ? { ...cleanStep, status: preservedStatus } : cleanStep;
    }),
  }));

  state.executionResults = null;
  state.overallStatus = null;
  state.automationError = null;
  state.screenshots = [];
  state.logs = [];
  state.executionFlow = [];
  state.testCompleted = false;
  state.hasExecutedInSession = false;

  state.replayProgressMap = {};
  state.replayExecutionResults = null;
  state.isReplayExecuting = false;
  state.replayCurrentStepIndex = -1;
  state.flatStepsForReplay = [];
}

/** Collect every step id present in the current instructions plan. */
function allStepIds(instructions) {
  const ids = [];
  for (const inst of instructions ?? []) {
    for (const s of inst.aiSteps ?? []) ids.push(s.id);
  }
  return ids;
}

/** Reconcile selection state against the current step universe.
 *  - drops ids from selectedStepIds that no longer exist
 *  - clears selectionAnchorId if it points to a removed id
 *  - dedupes selectedStepIds defensively
 */
function reconcileSelectionAgainstInstructions(state) {
  const universe = new Set(allStepIds(state.instructions));
  state.selectedStepIds = [...new Set(state.selectedStepIds)].filter(id => universe.has(id));
  if (state.selectionAnchorId && !universe.has(state.selectionAnchorId)) {
    state.selectionAnchorId = null;
  }
}

export const automationStudioSlice = createSlice({
  name: 'automationStudio',
  initialState,
  reducers: {
    // ── Instructions ─────────────────────────────────────────
    setInstructions: (state, action) => {
      state.instructions = action.payload;
      // Keep selectedStep the same *logical* step as the tree: after execution merges,
      // instructions hold fresh status/passed/screenshot; selectedStep must not stay stale.
      if (state.selectedStep?.id) {
        const id = state.selectedStep.id;
        for (const inst of state.instructions) {
          const found = inst.aiSteps?.find((s) => s.id === id);
          if (found) {
            state.selectedStep = found;
            break;
          }
        }
      }
      reconcileSelectionAgainstInstructions(state);
    },

    updateStepInInstructions: (state, action) => {
      const step = action.payload;
      state.instructions = state.instructions.map(inst => ({
        ...inst,
        aiSteps: inst.aiSteps?.map(s => s.id === step.id ? step : s),
      }));
      state.selectedStep = step;
    },

    deleteStepFromInstructions: (state, action) => {
      const stepId = action.payload;
      state.instructions = state.instructions.map(inst => ({
        ...inst,
        aiSteps: inst.aiSteps?.filter(s => s.id !== stepId),
      }));
      state.selectedStep = null;
      state.rightPanelView = 'INSTRUCTIONS_VIEW';
      reconcileSelectionAgainstInstructions(state);
    },

    addStepToInstruction: (state, action) => {
      const { instructionId, afterStepIndex, newStep } = action.payload;
      state.instructions = state.instructions.map(inst => {
        if (inst.id !== instructionId) return inst;
        const steps = [...inst.aiSteps];
        steps.splice(afterStepIndex + 1, 0, newStep);
        return { ...inst, aiSteps: steps };
      });
      state.selectedStep = newStep;
      state.rightPanelView = 'STEP_EDITOR_VIEW';
    },

    // ── Selected step ────────────────────────────────────────
    setSelectedStep: (state, action) => { state.selectedStep = action.payload; },

    // ── Single-step selection ────────────────────────────────
    selectStep: (state, action) => {
      const id = action.payload;
      state.selectedStepIds = [id];
      state.selectionAnchorId = id;
    },
    clearSelection: (state) => {
      state.selectedStepIds = [];
      state.selectionAnchorId = null;
    },
    setSelectionAfterPaste: (state, action) => {
      const ids = action.payload ?? [];
      state.selectedStepIds = [...new Set(ids)];
      state.selectionAnchorId = ids.length > 0 ? ids[ids.length - 1] : null;
      // Also point the legacy `selectedStep` to the first pasted step so the row
      // highlight and right panel follow the paste instead of staying on the
      // previously-active step (which would visually look like "two steps selected").
      if (ids.length > 0) {
        const firstId = ids[0];
        for (const inst of state.instructions) {
          const found = inst.aiSteps?.find((s) => s.id === firstId);
          if (found) {
            state.selectedStep = found;
            break;
          }
        }
      }
    },

    // ── Right panel view machine ──────────────────────────────
    navigateToStepEditor: (state, action) => {
      state.selectedStep = action.payload;
      state.rightPanelView = 'STEP_EDITOR_VIEW';
    },
    navigateToInstructions: (state) => {
      state.selectedStep = null;
      state.rightPanelView = 'INSTRUCTIONS_VIEW';
    },
    navigateToResults: (state) => {
      state.selectedStep = null;
      state.rightPanelView = 'EXECUTION_VIEW';
    },
    navigateToExecution: (state) => { state.rightPanelView = 'EXECUTION_VIEW'; },

    // ── UI flags ─────────────────────────────────────────────
    setIsGenerating: (state, action) => { state.isGenerating = action.payload; },
    setIsSavedExecutionRecord: (state, action) => { state.isSavedExecutionRecord = action.payload; },
    setTestCompleted: (state, action) => { state.testCompleted = action.payload; },
    setIsLoadingSavedPlan: (state, action) => { state.isLoadingSavedPlan = action.payload; },

    // ── Environment & model ──────────────────────────────────
    setSelectedEnvironment: (state, action) => { state.selectedEnvironment = action.payload; },
    setSelectedAIModel: (state, action) => { state.selectedAIModel = action.payload; },
    setSelectedAIModelIsAuto: (state, action) => { state.selectedAIModelIsAuto = action.payload; },

    // ── Replay ────────────────────────────────────────────────
    setReplayProgressMap: (state, action) => { state.replayProgressMap = action.payload; },
    setReplayExecutionResults: (state, action) => { state.replayExecutionResults = action.payload; },
    setIsReplayExecuting: (state, action) => { state.isReplayExecuting = action.payload; },
    setReplayCurrentStepIndex: (state, action) => { state.replayCurrentStepIndex = action.payload; },
    setFlatStepsForReplay: (state, action) => { state.flatStepsForReplay = action.payload; },

    // ── Context flags ─────────────────────────────────────────
    setIsLoadedFromTestcase: (state, action) => { state.isLoadedFromTestcase = action.payload; },
    setHasExecutedInSession: (state, action) => { state.hasExecutedInSession = action.payload; },

    // ── Data payloads ─────────────────────────────────────────
    setFileSuggestions: (state, action) => { state.fileSuggestions = action.payload; },
    setFileDescriptions: (state, action) => { state.fileDescriptions = action.payload; },
    setMcpToolsList: (state, action) => { state.mcpToolsList = action.payload; },
    setMcpToolMap: (state, action) => { state.mcpToolMap = action.payload; },

    // ── Execution results ────────────────────────────────────
    setExecutionResults: (state, action) => { state.executionResults = action.payload; },
    setOverallStatus: (state, action) => { state.overallStatus = action.payload; },
    setAutomationError: (state, action) => { state.automationError = action.payload; },
    setScreenshots: (state, action) => { state.screenshots = action.payload; },
    setLogs: (state, action) => { state.logs = action.payload; },
    setExecutionFlow: (state, action) => { state.executionFlow = action.payload; },

    // ── Reset ─────────────────────────────────────────────────

    // Clears only the execution run: strips per-step runtime fields and wipes
    // execution/replay state, but keeps the plan (instructions + aiSteps structure).
    // Used when plan is saved and user wants to reset to a freshly-loaded state.
    resetExecutionRun: (state) => {
      stripExecutionRuntimeAndReplayState(state);
      state.selectedStep = null;
      state.rightPanelView = 'INSTRUCTIONS_VIEW';
    },

    // Same data reset as resetExecutionRun but keeps right panel + selection (for Replay).
    prepareForReplayRun: (state) => {
      stripExecutionRuntimeAndReplayState(state);
    },

    /** Clear replay/execution runtime only — preserve plan step statuses (recording markers). */
    clearReplayRuntimeState: (state) => {
      state.testCompleted = false;
      state.hasExecutedInSession = false;
      state.replayProgressMap = {};
      state.replayExecutionResults = null;
      state.isReplayExecuting = false;
      state.replayCurrentStepIndex = -1;
      state.executionResults = null;
      state.overallStatus = null;
      state.automationError = null;
      state.screenshots = [];
      state.logs = [];
      state.executionFlow = [];
    },

    resetForNewTestCase: (state) => ({
      ...initialState,
      selectedEnvironment: state.selectedEnvironment,
      selectedAIModel: state.selectedAIModel,
      selectedAIModelIsAuto: state.selectedAIModelIsAuto,
      mcpToolsList: state.mcpToolsList,
      mcpToolMap: state.mcpToolMap,
      fileSuggestions: state.fileSuggestions,
      fileDescriptions: state.fileDescriptions,
    }),
    resetAllAutomationStudioState: (state) => ({
      ...initialState,
      selectedEnvironment: state.selectedEnvironment,
      selectedAIModel: state.selectedAIModel,
      selectedAIModelIsAuto: state.selectedAIModelIsAuto,
      mcpToolsList: state.mcpToolsList,
      mcpToolMap: state.mcpToolMap,
      fileSuggestions: state.fileSuggestions,
      fileDescriptions: state.fileDescriptions,
    }),
  },
});

export const {
  setInstructions, updateStepInInstructions, deleteStepFromInstructions, addStepToInstruction,
  setSelectedStep,
  selectStep, clearSelection, setSelectionAfterPaste,
  navigateToStepEditor, navigateToInstructions, navigateToResults, navigateToExecution,
  setIsGenerating, setIsSavedExecutionRecord, setTestCompleted, setIsLoadingSavedPlan,
  setSelectedEnvironment, setSelectedAIModel, setSelectedAIModelIsAuto,
  setReplayProgressMap, setReplayExecutionResults, setIsReplayExecuting,
  setReplayCurrentStepIndex, setFlatStepsForReplay,
  setIsLoadedFromTestcase, setHasExecutedInSession,
  setFileSuggestions, setFileDescriptions, setMcpToolsList, setMcpToolMap,
  setExecutionResults, setOverallStatus, setAutomationError,
  setScreenshots, setLogs, setExecutionFlow,
  resetExecutionRun, prepareForReplayRun, clearReplayRuntimeState,
  resetForNewTestCase, resetAllAutomationStudioState,
} = automationStudioSlice.actions;

export default automationStudioSlice.reducer;
